/**
 * Creates bidirectional mapping from original citation indices to sequential display indices.
 *
 * This utility enables citation renumbering for better UX when the AI skips citation numbers.
 * For example, if the AI cites [1], [2], [3], [5] (skipping [4]), the mapping will renumber
 * [5] to display as [4], making citations sequential for users.
 *
 * The mapping preserves backend source array integrity while providing clean UI presentation.
 */

export interface CitationMapping {
  /** Maps original citation index (e.g., 5) to sequential display index (e.g., 4) */
  origToDisplay: Map<number, number>
  /** Maps sequential display index (e.g., 4) back to original citation index (e.g., 5) */
  displayToOrig: Map<number, number>
}

/**
 * Build citation mapping for renumbering citations sequentially.
 *
 * @param usedCitations - Array of original citation indices in order of first appearance
 * @param sources - Source array from backend (to validate indices exist)
 * @returns Bidirectional mapping between original and display indices
 *
 * @example
 * const usedCitations = [1, 2, 3, 5]  // [4] was skipped by AI
 * const sources = [{...}, {...}, {...}, {...}, {...}]  // 5 sources
 *
 * createCitationMapping(usedCitations, sources)
 * // Returns:
 * // {
 * //   origToDisplay: Map { 1→1, 2→2, 3→3, 5→4 },
 * //   displayToOrig: Map { 1→1, 2→2, 3→3, 4→5 }
 * // }
 */
export function createCitationMapping(
  usedCitations: number[],
  sources: Array<{ documentId: string }> | undefined
): CitationMapping {
  const origToDisplay = new Map<number, number>()
  const displayToOrig = new Map<number, number>()

  let displayIndex = 1

  for (const origIndex of usedCitations) {
    // Only map citations that have valid sources
    // origIndex is 1-based, sources array is 0-based
    if (!sources || !sources[origIndex - 1]) {
      console.warn(
        `[CitationMapping] Skipping citation [${origIndex}] - no source found (sources length: ${sources?.length ?? 0})`
      )
      continue
    }

    origToDisplay.set(origIndex, displayIndex)
    displayToOrig.set(displayIndex, origIndex)
    displayIndex++
  }

  return { origToDisplay, displayToOrig }
}
